import { IconCrossSmall } from "central-icons/IconCrossSmall";
import {
    WorkflowEditFormTriggerContextType,
    type WorkflowEditFormTriggerContext,
} from "@/components/automations/automation-edit-form-data";
import { RepoFilteringContent, SCMHostSelector } from "@/components/automations/AutomationEditRepoCodeContext";
import { Button } from "@/components/flexkit/Button";
import { Text } from "@/components/podkit/typography/Text";
import { useEnvironmentClassEntries, type EnvironmentClassEntry } from "@/hooks/use-environment-class-entries";
import { useListProjects } from "@/queries/project-queries";
import { useListRunnerSCMIntegrations } from "@/queries/runner-configuration-queries";
import { deduplicateUrls, normalizeRepositoryUrl } from "@/utils/csv-import";
import type React from "react";
import { useCallback, useEffect, useId, useMemo, type FC } from "react";
import { RadioGroup, RadioGroupItem } from "@/components/podkit/forms/RadioListField";
import { useFlagAutomationsRepoListModeEnabled } from "@/hooks/use-flag";
import { RepositoryListContextContent } from "@/components/automations/AutomationEditRepoListCodeContext";
import { ProjectCombobox } from "@/components/projects/ProjectCombobox";
import { EnvironmentClassSelector } from "@/components/automations/EnvironmentClassSelector";
import { pluralizeWithCount } from "@/utils/strings";
import { withGitpodSize } from "@/assets/icons/utils";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { cn } from "@/components/podkit/lib/cn";

type WorkflowEditCodeContextProps = {
    context?: WorkflowEditFormTriggerContext;
    onChange: (context: WorkflowEditFormTriggerContext) => void;
    disabled?: boolean;
};

const IconCrossSmallWithSize = withGitpodSize(IconCrossSmall);

export const MAX_REPO_URLS = 100;

export const WorkflowEditCodeContext: FC<WorkflowEditCodeContextProps> = ({ context, onChange, disabled = false }) => {
    const { value: repoListModeEnabled } = useFlagAutomationsRepoListModeEnabled();
    const { environmentClassEntries } = useEnvironmentClassEntries({ enabled: true });

    // Fetch SCM integrations for the selected runner (only for RepositoriesSearch context)
    const runnerId =
        context?.type === WorkflowEditFormTriggerContextType.RepositoriesSearch ? context.runnerId : undefined;
    const { data: scmIntegrations } = useListRunnerSCMIntegrations(runnerId);

    // Set default SCM host when integrations load and no host is selected
    useEffect(() => {
        if (
            context?.type === WorkflowEditFormTriggerContextType.RepositoriesSearch &&
            scmIntegrations &&
            scmIntegrations.length > 0 &&
            !context.scmHost
        ) {
            onChange({
                ...context,
                scmHost: scmIntegrations[0].host,
            });
        }
    }, [context, scmIntegrations, onChange]);

    const CONTEXT_ITEMS = useMemo(() => {
        return {
            [WorkflowEditFormTriggerContextType.Projects]: {
                name: "Projects",
                description: "Run steps across your Ona projects",
                enabled: true,
            },
            [WorkflowEditFormTriggerContextType.RepositoriesSearch]: {
                name: repoListModeEnabled ? "Repositories - Search" : "Repositories",
                description: "Target code repositories by filter or search",
                enabled: true,
            },
            [WorkflowEditFormTriggerContextType.RepositoriesList]: {
                name: "Repositories - List",
                description: "Target code repositories by explicit list",
                enabled: repoListModeEnabled,
            },
        } as const;
    }, [repoListModeEnabled]);

    const handleTargetChange = (value: WorkflowEditFormTriggerContextType) => {
        let nextContext: WorkflowEditFormTriggerContext;
        switch (value) {
            case WorkflowEditFormTriggerContextType.Projects:
                nextContext = {
                    type: WorkflowEditFormTriggerContextType.Projects,
                    projectIds: [],
                };
                break;
            case WorkflowEditFormTriggerContextType.RepositoriesSearch: {
                const defaultEntry = environmentClassEntries[0];
                nextContext = {
                    type: WorkflowEditFormTriggerContextType.RepositoriesSearch,
                    repoSearchString: "",
                    scmHost: "",
                    runnerId: "",
                    environmentClassId: defaultEntry?.clazz.id ?? "",
                };
                break;
            }
            case WorkflowEditFormTriggerContextType.RepositoriesList: {
                const defaultEntry = environmentClassEntries[0];
                nextContext = {
                    type: WorkflowEditFormTriggerContextType.RepositoriesList,
                    repoUrls: [],
                    environmentClassId: defaultEntry?.clazz.id ?? "",
                };
                break;
            }
            default:
                nextContext = {
                    type: WorkflowEditFormTriggerContextType.Projects,
                    projectIds: [],
                };
                break;
        }
        onChange(nextContext);
    };

    const handleProjectSelectionChange = useCallback(
        (projectIds: string[]) => {
            if (context?.type === WorkflowEditFormTriggerContextType.Projects) {
                onChange({
                    ...context,
                    projectIds,
                });
            }
        },
        [context, onChange],
    );

    const handleEnvironmentClassSelectionChange = useCallback(
        (selectedEntry: EnvironmentClassEntry) => {
            if (context?.type === WorkflowEditFormTriggerContextType.RepositoriesSearch) {
                if (
                    context.runnerId !== selectedEntry.runner.runnerId ||
                    context.environmentClassId !== selectedEntry.clazz.id
                ) {
                    onChange({
                        ...context,
                        runnerId: selectedEntry.runner.runnerId,
                        environmentClassId: selectedEntry.clazz.id,
                    });
                }
            } else if (context?.type === WorkflowEditFormTriggerContextType.RepositoriesList) {
                if (context.environmentClassId !== selectedEntry.clazz.id) {
                    onChange({
                        ...context,
                        environmentClassId: selectedEntry.clazz.id,
                    });
                }
            }
        },
        [context, onChange],
    );

    const handleRemoveProject = useCallback(
        (projectId: string) => {
            if (context?.type === WorkflowEditFormTriggerContextType.Projects) {
                onChange({
                    ...context,
                    projectIds: context?.projectIds?.filter((id: string) => id !== projectId) || [],
                });
            }
        },
        [context, onChange],
    );

    const handleClearAllProjects = useCallback(() => {
        if (context?.type === WorkflowEditFormTriggerContextType.Projects) {
            onChange({
                ...context,
                projectIds: [],
            });
        }
    }, [context, onChange]);

    const handleAddProject = useCallback(
        (projectId: string) => {
            if (
                context?.type === WorkflowEditFormTriggerContextType.Projects &&
                !context.projectIds.includes(projectId)
            ) {
                handleProjectSelectionChange([...context.projectIds, projectId]);
            }
        },
        [context, handleProjectSelectionChange],
    );

    const handleAddRepoSearchString = useCallback(
        (filter: string) => {
            if (context?.type === WorkflowEditFormTriggerContextType.RepositoriesSearch) {
                onChange({ ...context, repoSearchString: filter });
            }
        },
        [context, onChange],
    );

    const handleClearRepoSearchString = useCallback(() => {
        if (context?.type === WorkflowEditFormTriggerContextType.RepositoriesSearch) {
            onChange({ ...context, repoSearchString: "" });
        }
    }, [context, onChange]);

    const handleScmHostChange = useCallback(
        (scmHost: string) => {
            if (context?.type === WorkflowEditFormTriggerContextType.RepositoriesSearch) {
                onChange({
                    ...context,
                    scmHost,
                });
            }
        },
        [context, onChange],
    );

    const handleAddRepoUrls = useCallback(
        (urls: string[]) => {
            if (context?.type === WorkflowEditFormTriggerContextType.RepositoriesList) {
                const normalized = urls.map(normalizeRepositoryUrl).filter(Boolean);
                const allUrls = [...context.repoUrls, ...normalized];
                const deduplicated = deduplicateUrls(allUrls).slice(0, MAX_REPO_URLS);
                onChange({
                    ...context,
                    repoUrls: deduplicated,
                });
            }
        },
        [context, onChange],
    );

    const handleRemoveRepoUrl = useCallback(
        (url: string) => {
            if (context?.type === WorkflowEditFormTriggerContextType.RepositoriesList) {
                onChange({
                    ...context,
                    repoUrls: context.repoUrls.filter((u) => u !== url),
                });
            }
        },
        [context, onChange],
    );

    const handleClearAllRepoUrls = useCallback(() => {
        if (context?.type === WorkflowEditFormTriggerContextType.RepositoriesList) {
            onChange({
                ...context,
                repoUrls: [],
            });
        }
    }, [context, onChange]);

    // MARK: render ctx selectors

    let contextSelectors: React.ReactNode;
    let projectSelector: React.ReactNode = null;
    switch (context?.type) {
        case WorkflowEditFormTriggerContextType.Projects:
            projectSelector = (
                <ProjectCombobox
                    selectedProject={null}
                    onProjectChange={handleAddProject}
                    excludeProjectIds={context.projectIds}
                    placeholder="Add a project…"
                    className="w-fit"
                    disabled={disabled}
                />
            );
            break;
        case WorkflowEditFormTriggerContextType.RepositoriesSearch:
            contextSelectors = (
                <div className="flex min-w-0 flex-row items-center gap-4">
                    <EnvironmentClassSelector
                        selectedEnvironmentClassId={context.environmentClassId}
                        disabled={disabled}
                        onSelectionChange={handleEnvironmentClassSelectionChange}
                    />
                    {scmIntegrations && scmIntegrations.length > 1 && (
                        <SCMHostSelector
                            scmIntegrations={scmIntegrations}
                            selectedHost={context.scmHost}
                            onHostChange={handleScmHostChange}
                            disabled={disabled}
                        />
                    )}
                </div>
            );
            break;
        case WorkflowEditFormTriggerContextType.RepositoriesList:
            contextSelectors = (
                <EnvironmentClassSelector
                    selectedEnvironmentClassId={context.environmentClassId}
                    disabled={disabled}
                    onSelectionChange={handleEnvironmentClassSelectionChange}
                />
            );
            break;
    }

    // displays the selected context, eg selected projects or repository search string
    let selectedContextContent: React.ReactNode;
    switch (context?.type) {
        case WorkflowEditFormTriggerContextType.Projects:
            selectedContextContent = (
                <SelectedProjectsContent
                    selectedProjectIds={context.projectIds}
                    disabled={disabled}
                    onRemoveProject={handleRemoveProject}
                    onClearAll={handleClearAllProjects}
                />
            );
            break;
        case WorkflowEditFormTriggerContextType.RepositoriesSearch: {
            if (context?.runnerId) {
                selectedContextContent = (
                    <RepoFilteringContent
                        disabled={disabled}
                        runnerId={context?.runnerId}
                        repoSearchString={context?.repoSearchString}
                        scmHost={context?.scmHost}
                        onAddFilter={handleAddRepoSearchString}
                        onClearFilter={handleClearRepoSearchString}
                    />
                );
            } else {
                selectedContextContent = null;
            }
            break;
        }
        case WorkflowEditFormTriggerContextType.RepositoriesList:
            selectedContextContent = (
                <RepositoryListContextContent
                    context={context}
                    runnerId={
                        environmentClassEntries.find((ev) => ev.clazz.id === context.environmentClassId)?.clazz
                            .runnerId ?? ""
                    }
                    disabled={disabled}
                    onAddUrls={handleAddRepoUrls}
                    onRemoveUrl={handleRemoveRepoUrl}
                    onClearAll={handleClearAllRepoUrls}
                />
            );
            break;
        default:
            selectedContextContent = null;
            break;
    }

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-row items-end justify-between gap-4">
                <div className="flex flex-col gap-2">
                    <Text className="text-base font-medium text-content-primary">Runs on</Text>
                    <RadioGroup
                        value={context?.type}
                        onValueChange={(value) => handleTargetChange(value as WorkflowEditFormTriggerContextType)}
                        disabled={disabled}
                        className="flex flex-row items-center gap-4"
                    >
                        {Object.entries(CONTEXT_ITEMS).map(([value, item]) => {
                            if (!item.enabled) {
                                return null;
                            }
                            return (
                                <label key={value} className="flex cursor-pointer items-center gap-2">
                                    <RadioGroupItem value={value} disabled={!item.enabled} />
                                    <Text className="text-base font-medium text-content-primary">{item.name}</Text>
                                </label>
                            );
                        })}
                    </RadioGroup>
                </div>
                {projectSelector}
            </div>
            {contextSelectors && <div className="flex flex-row items-center">{contextSelectors}</div>}
            {selectedContextContent}
        </div>
    );
};

// MARK: project code context

const SelectedProjectsContent: FC<{
    selectedProjectIds: string[];
    disabled: boolean;
    onRemoveProject: (projectId: string) => void;
    onClearAll: () => void;
}> = ({ selectedProjectIds, onRemoveProject, onClearAll }) => {
    const { data: projectsData, isLoading } = useListProjects({
        enabled: selectedProjectIds.length > 0,
        projectIds: selectedProjectIds,
    });
    const projectListLabelId = useId();

    const isEmpty = selectedProjectIds.length === 0 || (projectsData?.projects.length === 0 && !isLoading);

    if (isEmpty) {
        return (
            <div
                className={cn(
                    "flex min-h-[46px] w-full flex-row items-start gap-2 rounded-lg border px-3 py-2 transition-colors ease-out",
                    isEmpty ? "border-border-subtle" : "border-border-base",
                )}
            >
                <Text className="flex h-[30px] items-center text-base text-content-muted">
                    Add projects to run the automation for each selected project.
                </Text>
            </div>
        );
    }

    return (
        <div className="flex flex-col rounded-lg border border-border-base">
            <div className="flex flex-row items-center justify-between rounded-t-lg border-b border-border-base bg-surface-base py-0.5 pl-4 pr-2">
                <Text className="text-sm font-medium text-content-strong" id={projectListLabelId}>
                    {pluralizeWithCount(selectedProjectIds.length, false, "project")}
                </Text>
                <Button
                    variant="ghost"
                    size="xs"
                    onClick={onClearAll}
                    data-tracking-id="clear-all-projects-automation-project-trigger-automation-edit-code-context"
                >
                    Clear all
                </Button>
            </div>
            <ScrollArea>
                <ul
                    className="max-h-80 divide-y divide-border-subtle overflow-y-auto"
                    aria-labelledby={projectListLabelId}
                >
                    {projectsData?.projects.map((project) => (
                        <li key={project.id} className="flex flex-row items-center justify-between py-1 pl-4 pr-1">
                            <Text className="text-base font-medium">{project.metadata?.name}</Text>
                            <Button
                                variant="ghost"
                                size="sm"
                                LeadingIcon={IconCrossSmallWithSize}
                                aria-label={`Remove '${project.metadata?.name}' project`}
                                onClick={() => onRemoveProject(project.id)}
                                data-tracking-id="remove-project-automation-project-trigger-automation-edit-code-context"
                            />
                        </li>
                    ))}
                </ul>
            </ScrollArea>
        </div>
    );
};
